import { WorkflowData } from "../WorkflowEditor";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Plus, Trash2, GitBranch } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ConditionalRule {
  id: string;
  condition_field: string;
  operator: string;
  value: string;
  action_type: 'add_phase' | 'skip_phase' | 'modify_phase' | 'add_document';
  action_config: any;
  description: string;
}

interface ConditionalLogicTabProps {
  data: WorkflowData;
  onChange: (data: WorkflowData) => void;
}

export function ConditionalLogicTab({ data, onChange }: ConditionalLogicTabProps) {
  const conditionalRules = (data as any).conditional_rules || [];

  const addRule = () => {
    const newRule: ConditionalRule = {
      id: `rule-${Date.now()}`,
      condition_field: '',
      operator: 'equals',
      value: '',
      action_type: 'add_phase',
      action_config: {},
      description: '',
    };

    onChange({
      ...data,
      conditional_rules: [...conditionalRules, newRule],
    } as any);
  };

  const updateRule = (index: number, updatedRule: ConditionalRule) => {
    const updated = [...conditionalRules];
    updated[index] = updatedRule;
    onChange({
      ...data,
      conditional_rules: updated,
    } as any);
  };

  const removeRule = (index: number) => {
    const updated = conditionalRules.filter((_: any, i: number) => i !== index);
    onChange({
      ...data,
      conditional_rules: updated,
    } as any);
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <h2 className="text-lg font-semibold flex items-center gap-2">
          <GitBranch className="h-5 w-5" />
          Conditional Logic (Advanced)
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          Define conditional branching based on user responses. Workflow paths will adapt automatically.
        </p>
      </div>

      {conditionalRules.length === 0 ? (
        <Card className="p-8 text-center">
          <GitBranch className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
          <p className="text-muted-foreground mb-4">
            No conditional rules defined yet
          </p>
          <Button onClick={addRule}>
            <Plus className="h-4 w-4 mr-2" />
            Add First Rule
          </Button>
        </Card>
      ) : (
        <div className="space-y-4">
          {conditionalRules.map((rule: ConditionalRule, index: number) => (
            <Card key={rule.id} className="p-4 space-y-4">
              <div className="flex items-center justify-between">
                <Badge variant="outline">Rule {index + 1}</Badge>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRule(index)}
                >
                  <Trash2 className="h-4 w-4 text-red-500" />
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Field</Label>
                  <Input
                    placeholder="e.g., member_age"
                    value={rule.condition_field}
                    onChange={(e) => updateRule(index, { ...rule, condition_field: e.target.value })}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Operator</Label>
                  <Select
                    value={rule.operator}
                    onValueChange={(value) => updateRule(index, { ...rule, operator: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="equals">Equals</SelectItem>
                      <SelectItem value="not_equals">Not Equals</SelectItem>
                      <SelectItem value="greater_than">Greater Than</SelectItem>
                      <SelectItem value="less_than">Less Than</SelectItem>
                      <SelectItem value="contains">Contains</SelectItem>
                      <SelectItem value="is_true">Is True</SelectItem>
                      <SelectItem value="is_false">Is False</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Value</Label>
                  <Input
                    placeholder="e.g., 18"
                    value={rule.value}
                    onChange={(e) => updateRule(index, { ...rule, value: e.target.value })}
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Then Action</Label>
                  <Select
                    value={rule.action_type}
                    onValueChange={(value: any) => updateRule(index, { ...rule, action_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="add_phase">Add Extra Phase</SelectItem>
                      <SelectItem value="skip_phase">Skip Phase</SelectItem>
                      <SelectItem value="modify_phase">Modify Phase</SelectItem>
                      <SelectItem value="add_document">Add Required Document</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Description</Label>
                  <Input
                    placeholder="e.g., Add guardian consent phase for minors"
                    value={rule.description}
                    onChange={(e) => updateRule(index, { ...rule, description: e.target.value })}
                  />
                </div>
              </div>

              {rule.action_type === 'add_phase' && (
                <div className="space-y-2 bg-muted p-3 rounded">
                  <Label className="text-xs">Phase Configuration</Label>
                  <Textarea
                    placeholder="Enter phase configuration as JSON"
                    value={JSON.stringify(rule.action_config, null, 2)}
                    onChange={(e) => {
                      try {
                        const config = JSON.parse(e.target.value);
                        updateRule(index, { ...rule, action_config: config });
                      } catch {}
                    }}
                    rows={4}
                    className="font-mono text-xs"
                  />
                </div>
              )}
            </Card>
          ))}

          <Button onClick={addRule} variant="outline" className="w-full">
            <Plus className="h-4 w-4 mr-2" />
            Add Another Rule
          </Button>
        </div>
      )}

      <Card className="p-4 bg-blue-50 dark:bg-blue-950 border-blue-200">
        <h3 className="font-medium text-sm mb-2">💡 Example Use Cases</h3>
        <ul className="text-sm text-muted-foreground space-y-1">
          <li>• If member is minor → Add guardian consent phase</li>
          <li>• If property value &gt; $500k → Add additional appraisal document</li>
          <li>• If married = true → Add spouse consent documents</li>
          <li>• If has_llc = true → Skip individual business setup phase</li>
        </ul>
      </Card>
    </div>
  );
}
